/**
 * Derives the public projection (ProfileSummary, TeamSnapshot) from the opaque game state.
 *
 * The game state is the serialized `player` object of the ported game (field names of FWP.as:434-450 and
 * create_new_params, FWP.as:17801-18021). Values the projector recognizes override client-supplied summary
 * fields; unknown layouts fall back to the client values. Both snake_case (original) and camelCase
 * (docs/analysis/backend-requirements.md §2.2) spellings are accepted, as are `{ value }` wrappers left by
 * ProtectedInt.
 */
import type { JsonValue } from '../../shared/json';
import { type ProfileSummary, type TeamSnapshotInput, emptyProfileSummary, PROFILE_SUMMARY_KEYS } from '../../shared/protocol/model';

export interface ProfileProjector {
  summary(gameState: JsonValue | null, clientSummary: Partial<ProfileSummary> | undefined, previous: ProfileSummary | null): ProfileSummary;
  team(gameState: JsonValue | null, clientTeam: TeamSnapshotInput | undefined): TeamSnapshotInput;
}

type Rec = Record<string, unknown>;

function rec(v: unknown): Rec | null {
  return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Rec) : null;
}

/**
 * Number from a plain number, a `{ value: number }` wrapper (ProtectedInt) or the game's JSON-state encoding of a
 * ProtectedInt (`{ $pi: number }`, src/game/runtime/as3.ts toJsonState).
 */
export function numField(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const r = rec(v);
  if (r && typeof r.value === 'number' && Number.isFinite(r.value)) return r.value;
  if (r && typeof r.$pi === 'number' && Number.isFinite(r.$pi)) return r.$pi;
  return undefined;
}

/**
 * The `player` record inside a game state. The ported game saves `{ format: "fwp-state-v1", player, map_info }`
 * (FWP.serialize_state); a bare player object (tests, imports) is accepted as well.
 */
export function playerRecord(gameState: unknown): Rec | null {
  const st = rec(gameState);
  if (st && typeof st.format === 'string' && st.format.startsWith('fwp-state-')) return rec(st.player);
  return st;
}

function pick(state: Rec, ...names: string[]): number | undefined {
  for (const n of names) {
    const v = numField(state[n]);
    if (v !== undefined) return v;
  }
  return undefined;
}

function clampCounter(n: number): number {
  return Math.max(0, Math.min(777_599_999, Math.floor(n)));
}

export class DefaultProfileProjector implements ProfileProjector {
  constructor(private readonly levelOf: ((exp: number) => number) | null) {}

  summary(gameState: JsonValue | null, client: Partial<ProfileSummary> | undefined, previous: ProfileSummary | null): ProfileSummary {
    const out: ProfileSummary = { ...(previous ?? emptyProfileSummary()) };
    if (client) for (const k of PROFILE_SUMMARY_KEYS) if (typeof client[k] === 'number') out[k] = client[k]!;
    const st = playerRecord(gameState);
    if (st) {
      const set = (k: keyof ProfileSummary, v: number | undefined) => {
        if (v !== undefined) out[k] = v;
      };
      set('exp', pick(st, 'exp'));
      const rating = pick(st, 'rating');
      if (rating !== undefined) out.rating = rating + (pick(st, 'achiev_rating_bonus', 'achievRatingBonus') ?? 0);
      set('assaults', numField(rec(st.achiev)?.assault));
      set('attack', pick(st, 'attack'));
      set('protect', pick(st, 'protect'));
      set('health', pick(st, 'health'));
      set('worms', pick(st, 'worms'));
      set('spec', pick(st, 'spec'));
      set('careerMax', pick(st, 'career_max', 'careerMax'));
      set('missionsMax', pick(st, 'missions_max', 'missionsMax'));
      set('survivalMax', pick(st, 'survival_max', 'survivalMax'));
      set('bossMax', pick(st, 'boss_max', 'bossMax'));
      const lvl = pick(st, 'level');
      if (lvl !== undefined) out.level = lvl;
    }
    if (this.levelOf && (st?.exp !== undefined || client?.exp !== undefined)) out.level = this.levelOf(out.exp);
    for (const k of PROFILE_SUMMARY_KEYS) out[k] = clampCounter(out[k]);
    if (out.level < 1) out.level = 1;
    return out;
  }

  team(gameState: JsonValue | null, client: TeamSnapshotInput | undefined): TeamSnapshotInput {
    const st = playerRecord(gameState);
    const out: TeamSnapshotInput = {};
    if (st) {
      const perks = rec(st.perks);
      if (perks) {
        out.perks = {};
        for (const [k, v] of Object.entries(perks)) {
          const n = numField(v);
          if (n !== undefined && /^[A-Za-z0-9_]{1,48}$/.test(k)) out.perks[k] = n;
        }
      }
      const weapons = rec(st.weapons);
      if (weapons) {
        out.weapons = {};
        for (const [k, v] of Object.entries(weapons)) {
          if (!Array.isArray(v) || !/^[A-Za-z0-9_]{1,48}$/.test(k)) continue;
          // Original layout: [count, {range, stolen, crafted}, ...] (an owned-nothing entry is [0, {}], FWP.as:17337);
          // other layout: [{range, ...}, ...].
          const count = numField(v[0]);
          const n = count !== undefined ? count : v.filter((x) => rec(x) !== null).length;
          if (n > 0) out.weapons[k] = n;
        }
      }
      const names: string[] = [];
      if (Array.isArray(st.wormNames)) {
        for (const n of st.wormNames) if (typeof n === 'string') names.push(n.slice(0, 15));
      } else {
        for (let i = 1; i <= 5; i++) {
          const n = st[`worm_name${i}`];
          if (typeof n === 'string') names.push(n.slice(0, 15));
        }
      }
      if (names.length) out.wormNames = names;
      for (const [k, names2] of [
        ['attack', ['attack']],
        ['protect', ['protect']],
        ['health', ['health']],
        ['worms', ['worms']],
        ['spec', ['spec']],
        ['implant', ['cur_impl', 'curImpl']],
        ['flag', ['cur_flag', 'curFlag']],
      ] as const) {
        const v = pick(st, ...names2);
        if (v !== undefined) (out as Record<string, unknown>)[k] = v;
      }
    }
    return { ...out, ...(client ?? {}) };
  }
}
